// The conversation before a task's first message, a page at a time from its end: "Show history" loads the turns just
// before the task, "Load earlier" the page before those, keeping the reader's place. On the result page it sits above
// the replay in the same scroll box; on the task page it has a box of its own.
import { esc, get } from '../core'
import { renderTranscript, wireTranscript } from '../transcript'
import { compact } from './compact'

const PAGE = 20

interface State { messages: unknown[]; start: number; total: number }

/** The placeholder wireHistory fills in. */
export function historyMount(): string {
  return '<div class="rp-hist"><button type="button" class="btn rp-hist-show">Show history</button></div>'
}

/** Wire every history placeholder under `root` for task `taskId`. `own`: the messages scroll in a box of their own,
 *  else in the scroll box around them (the replay's). */
export function wireHistory(root: HTMLElement, taskId: string, own: boolean): void {
  Array.prototype.forEach.call(root.querySelectorAll('.rp-hist'), (box: HTMLElement) => {
    const show = box.querySelector('.rp-hist-show') as HTMLButtonElement | null
    if (show) show.onclick = () => load(box, taskId, own, null)
  })
}

const pages = new Map<string, State>()

function load(box: HTMLElement, taskId: string, own: boolean, prev: State | null): void {
  const before = prev ? prev.start : undefined
  const cached = !prev ? pages.get(taskId) : undefined
  if (cached) { paint(box, taskId, own, cached, true); return }
  const btn = box.querySelector('button') as HTMLButtonElement | null
  if (btn) { btn.disabled = true; btn.textContent = 'Loading…' }
  get('/api/replay/tasks/' + encodeURIComponent(taskId) + '/history?limit=' + PAGE + (before !== undefined ? '&before=' + before : ''))
    .then((h: any) => {
      if (!box.isConnected) return
      const page: State = { messages: Array.isArray(h?.messages) ? h.messages : [], start: Number(h?.start ?? 0), total: Number(h?.total ?? 0) }
      const state = prev ? { messages: [...page.messages, ...prev.messages], start: page.start, total: page.total } : page
      pages.set(taskId, state)
      paint(box, taskId, own, state, !prev)
    })
    .catch(() => { if (box.isConnected) box.innerHTML = '<div class="empty">Could not load the history.</div>' })
}

/** Draw the loaded messages. The first load shows the end, leading into the task; a later one keeps the reader on
 *  the message they were at, however much was added above it. */
function paint(box: HTMLElement, taskId: string, own: boolean, s: State, first: boolean): void {
  if (!s.total) { box.innerHTML = '<div class="rp-hist-bar tr-muted">No conversation before the task.</div>'; return }
  const scroller = own ? (box.querySelector('.rp-hist-msgs') as HTMLElement | null) : (box.closest('.rp-scroll') as HTMLElement | null)
  const was = scroller ? scroller.scrollHeight - scroller.scrollTop : 0
  box.innerHTML =
    '<div class="rp-hist-bar">' +
    (s.start > 0 ? '<button type="button" class="btn rp-hist-more">Load ' + Math.min(PAGE, s.start) + ' earlier</button>' : '') +
    '<span class="tr-muted">' + (s.start > 0 ? 'last ' + s.messages.length + ' of ' : '') + esc(String(s.total)) + ' messages before the task</span>' +
    '<button type="button" class="rp-more rp-hist-hide">Hide</button></div>' +
    '<div class="rp-hist-msgs' + (own ? ' tr-card rp-scroll rp-tx' : '') + '">' + renderTranscript(compact(s.messages as any[])) + '</div>' +
    '<div class="rp-hist-sep"><span>The task starts here</span></div>'
  const msgs = box.querySelector('.rp-hist-msgs') as HTMLElement
  if (own) wireTranscript(msgs)
  else if (scroller) wireTranscript(scroller)
  if (first) {
    if (own) msgs.scrollTop = msgs.scrollHeight
    else if (scroller) {
      const sep = box.querySelector('.rp-hist-sep') as HTMLElement
      scroller.scrollTop = Math.max(0, sep.offsetTop - scroller.offsetTop - scroller.clientHeight + 120)
    }
  } else {
    const at = own ? msgs : scroller
    if (at) at.scrollTop = at.scrollHeight - was
  }
  const more = box.querySelector('.rp-hist-more') as HTMLElement | null
  if (more) more.onclick = () => load(box, taskId, own, s)
  ;(box.querySelector('.rp-hist-hide') as HTMLElement).onclick = () => {
    box.innerHTML = '<button type="button" class="btn rp-hist-show">Show history</button>'
    ;(box.querySelector('.rp-hist-show') as HTMLElement).onclick = () => load(box, taskId, own, null)
  }
}
